/**
 * M7 S43: the restarted derelict's look (plan S43; spec §16.4, §16.2 the Candle).
 *
 * A derelict POI is drawn from the FR station kit (`render/environment/derelict.ts`); no new model. Its look is a
 * small bit mask of light groups the builder already carries (hidden by default):
 * - **windows** (warm, steady), **beacon** (a live blinking beacon), **dock** (the docking light), and **window3**
 *   (the Candle's third window).
 * - `surveyed` / `kit` / `reverted`: dark (a derelict). `lit` (online, not yet registered): windows and the beacon.
 *   `title`, `station`, `handedOver`: windows, beacon and the docking light. `dark` (unpowered, spec §16.4 lapse):
 *   nothing; production and services are closed, so is the dock.
 * - **The Harrow (the Candle)** is never restarted: the Watch keeps two windows lit; it goes dark after a betrayal or
 *   when the Candle went dark (Blackmail, `candleDarkOnlineDay` / world flag `candle_dark`); the third window lights
 *   only while gate stability is at or above `candle.thirdWindowStability`. **It reads nothing a Candle run writes**
 *   (PC-7: supplying never changes it).
 *
 * `derelictLooks` runs at event rate (a Career change); the renderer compares masks per view and writes the scene
 * only when one changes.
 */
import { z } from 'zod'
import type { Career } from '../../career/types'
import { storyOf } from '../../story/state'
import type { ReactivationState } from '../../story/types'
import lookJson from './look.json'

export const LOOK_WINDOWS = 1
export const LOOK_WINDOW3 = 2
export const LOOK_BEACON = 4
export const LOOK_DOCK = 8
/** A derelict: no lights. */
export const LOOK_DARK = 0

const vec3 = z.tuple([z.number(), z.number(), z.number()])
const group = z.strictObject({ color: z.string().regex(/^#[0-9A-Fa-f]{6}$/), blinkHz: z.number().nonnegative().optional(), positions: z.array(vec3).min(1) })
export const lookConfigSchema = z.strictObject({
  comment: z.string(),
  lights: z.strictObject({ windows: group, window3: group, beacon: group, dock: group }),
  candle: z.strictObject({ thirdWindowStability: z.number().min(0).max(100) }),
})
export type LookConfig = z.output<typeof lookConfigSchema>
export type LookLightGroup = keyof LookConfig['lights']

/** The bit of each light group. */
export const LOOK_BITS: Readonly<Record<LookLightGroup, number>> = { windows: LOOK_WINDOWS, window3: LOOK_WINDOW3, beacon: LOOK_BEACON, dock: LOOK_DOCK }

let cached: LookConfig | undefined
export function lookConfig(): LookConfig {
  cached ??= lookConfigSchema.parse(lookJson)
  return cached
}

const MASK_OF: Readonly<Record<ReactivationState, number>> = {
  surveyed: LOOK_DARK,
  kit: LOOK_DARK,
  reverted: LOOK_DARK,
  dark: LOOK_DARK,
  lit: LOOK_WINDOWS | LOOK_BEACON,
  title: LOOK_WINDOWS | LOOK_BEACON | LOOK_DOCK,
  station: LOOK_WINDOWS | LOOK_BEACON | LOOK_DOCK,
  handedOver: LOOK_WINDOWS | LOOK_BEACON | LOOK_DOCK,
}

/** The look of a reactivation state. */
export function restartLook(state: ReactivationState): number {
  return MASK_OF[state]
}

/** What the Candle's look reads (and nothing else: never the Candle runs). */
export interface CandleInput {
  /** Gate stability 0–100 (the worker's gate view), or null when unknown (the third window stays dark). */
  stability: number | null
  /** The world flag `candle_dark` (the worker), when known. */
  candleDark?: boolean
}

/**
 * The Candle's input from the worker's story view (its `stability` and the world flag `candle_dark` as `candleDark`),
 * or the unknown input before the first read (no third window, not dark by the flag).
 */
export function candleInputOf(view: { readonly stability: number; readonly candleDark?: boolean } | null): CandleInput {
  return view === null ? { stability: null, candleDark: false } : { stability: view.stability, candleDark: view.candleDark === true }
}

/** The Harrow's look (spec §16.2, canon first-rush.md §8 #7): two windows, dark after a betrayal or the Candle-dark. */
export function candleLook(career: Career, input: CandleInput, cfg: LookConfig = lookConfig()): number {
  const story = storyOf(career)
  if (story.flags.watchers_betrayed === true) return LOOK_DARK
  if (story.finale.candleDarkOnlineDay !== undefined || input.candleDark === true) return LOOK_DARK
  const third = input.stability !== null && input.stability >= cfg.candle.thirdWindowStability
  return LOOK_WINDOWS | (third ? LOOK_WINDOW3 : 0)
}

/**
 * The look of every derelict POI with a reactivation record, plus the Harrow's (`harrowPoiId`), keyed by POI id.
 * POIs not in the map are plain derelicts (`LOOK_DARK`). Event rate.
 */
export function derelictLooks(career: Career, harrowPoiId: string | null, candle: CandleInput, cfg: LookConfig = lookConfig()): Map<string, number> {
  const out = new Map<string, number>()
  for (const r of storyOf(career).reactivations) out.set(r.poiId, restartLook(r.state))
  if (harrowPoiId !== null) out.set(harrowPoiId, candleLook(career, candle, cfg))
  return out
}

/**
 * The renderer's side: the looks the story link publishes. `version` moves on every change, so a reader compares
 * one number per frame and re-reads masks only when it moved.
 */
export interface DerelictLookSource {
  readonly version: number
  look(poiId: string): number
}

export interface DerelictLookStore extends DerelictLookSource {
  /** Replaces the looks; bumps `version` only when a mask changed. */
  set(next: ReadonlyMap<string, number>): void
}

export function createDerelictLookStore(): DerelictLookStore {
  let masks: ReadonlyMap<string, number> = new Map()
  let version = 0
  return {
    get version(): number {
      return version
    },
    look(poiId: string): number {
      return masks.get(poiId) ?? LOOK_DARK
    },
    set(next: ReadonlyMap<string, number>): void {
      let changed = next.size !== masks.size
      if (!changed) for (const [k, v] of next) if (masks.get(k) !== v) changed = true
      if (!changed) return
      masks = next
      version++
    },
  }
}

/** The shared store the story link writes and the structures feature reads (null-safe: empty until a career runs). */
export const derelictLookStore: DerelictLookStore = createDerelictLookStore()
